/**
 * TopBar — breadcrumbs, app drawer, and user info for the top navigation bar.
 */

import React, { useState, useEffect } from 'react'

import { UserInfo } from '@elsapiens/layout'
import { usePage } from '@elsapiens/providers'
import { AppDrawer } from '@elsapiens/shell'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@elsapiens/ui'
import { Link , useLocation } from 'react-router-dom'


import { AUTH_BASE_URL } from './layout-helpers'
import { NotificationBell } from './NotificationBell'
import { AuthService } from '../../services/auth-service'
import { getAllWorkspaces, getCurrentWorkspace, clearWorkspaceCache, type WorkspaceInfo } from '../../services/workspace-service'
import { useFavorites } from '../../stores/favorites-store'
import { useWorkspaceAppsFromBackend } from '../../hooks/use-workspace-apps'

function BreadcrumbNav(): React.JSX.Element | null {
  const { breadcrumbs } = usePage()

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null
  }

  return (
    <Breadcrumb className="hidden sm:block">
      <BreadcrumbList>
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1
          return (
            <React.Fragment key={index}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {(() => {
                  if (isLast) { return <BreadcrumbPage>{crumb.label}</BreadcrumbPage> }
                  if (crumb.href) {
                    return (
                      <BreadcrumbLink as={Link} href={crumb.href}>
                        {crumb.label}
                      </BreadcrumbLink>
                    )
                  }
                  return <BreadcrumbLink>{crumb.label}</BreadcrumbLink>
                })()}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

interface UserProfile {
  displayName: string
  email: string
  phone: string
  avatar: string
}

interface ElAuthMeResponse {
  person_id?: string
  name?: { first_name?: string; last_name?: string }
  verified_handles?: Array<{
    type: string
    is_primary?: boolean
    value?: string | { country_code: string; phone_number: string }
  }>
}

function buildFullName(data: ElAuthMeResponse): string {
  const firstName = data.name?.first_name ?? ''
  const lastName = data.name?.last_name ?? ''
  return [firstName, lastName].filter(Boolean).join(' ')
}

function pickPrimaryEmail(data: ElAuthMeResponse): string {
  const emailHandle = data.verified_handles?.find(h => h.type === 'email' && h.is_primary)
    ?? data.verified_handles?.find(h => h.type === 'email')
  return typeof emailHandle?.value === 'string' ? emailHandle.value : ''
}

function pickPhoneString(data: ElAuthMeResponse): string {
  const phoneHandle = data.verified_handles?.find(h => h.type === 'phone')
  const phoneVal = phoneHandle?.value
  if (phoneVal && typeof phoneVal === 'object') {
    return `${phoneVal.country_code} ${phoneVal.phone_number}`
  }
  return ''
}

async function fetchAuthProfile(): Promise<UserProfile> {
  try {
    const resp = await fetch('/auth-api/me', { credentials: 'include' })
    if (!resp.ok) { throw new Error('auth profile fetch failed') }
    const json = await resp.json() as { data?: ElAuthMeResponse }
    const data: ElAuthMeResponse = json.data ?? (json as ElAuthMeResponse)
    if (!data?.person_id) { throw new Error('no person_id in response') }

    const fullName = buildFullName(data)
    const primaryEmail = pickPrimaryEmail(data)
    const phoneStr = pickPhoneString(data)

    return { displayName: fullName || primaryEmail || 'User', email: primaryEmail, phone: phoneStr, avatar: '' }
  } catch {
    return { displayName: 'User', email: '', phone: '', avatar: '' }
  }
}

function useTopBarData(): { userProfile: UserProfile; entityName: string; workspaces: WorkspaceInfo[] } {
  const [userProfile, setUserProfile] = useState<UserProfile>({ displayName: '', email: '', phone: '', avatar: '' })
  const [entityName, setEntityName] = useState<string>('')
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([])

  useEffect(() => {
    const userContext = AuthService.getUserContext()
    setEntityName(userContext.tag ?? '')

    void fetchAuthProfile().then(setUserProfile)

    void getCurrentWorkspace().then(ws => {
      if (ws?.name) { setEntityName(ws.name) }
    }).catch(() => {})

    void getAllWorkspaces().then(ws => {
      if (ws.length > 1) { setWorkspaces(ws) }
    }).catch(() => {})
  }, [])

  return { userProfile, entityName, workspaces }
}

interface WorkspaceMenuItem {
  label: string
  onClick: () => void
  icon: React.JSX.Element
}

function buildWorkspaceMenuItems(workspaces: WorkspaceInfo[], onSwitch: () => void): WorkspaceMenuItem[] {
  if (workspaces.length === 0) { return [] }
  return [{
    label: 'Switch Workspace',
    onClick: onSwitch,
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
      </svg>
    ),
  }]
}

function BookmarkButton(): React.JSX.Element | null {
  const { pageTitle, breadcrumbs } = usePage()
  const { pathname } = useLocation()
  const { toggle, isFavorite } = useFavorites()

  // Don't show on root or hub pages that aren't leaf actions
  const EXCLUDED = new Set(['/', '/accounting', '/sales', '/purchases', '/banking', '/reports', '/period-end', '/admin'])
  if (EXCLUDED.has(pathname) || !pageTitle) {return null}

  const label = breadcrumbs?.at(-1)?.label ?? pageTitle
  const isPinned = isFavorite(pathname)

  return (
    <button
      aria-label={isPinned ? `Remove ${label} from quickbar` : `Add ${label} to quickbar`}
      className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      title={isPinned ? 'Remove from quickbar' : 'Pin to quickbar'}
      onClick={() => toggle({ id: pathname, path: pathname, label })}
    >
      {isPinned ? (
        <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      )}
    </button>
  )
}

export function TopBarContent(): React.JSX.Element {
  const { userProfile, entityName, workspaces } = useTopBarData()
  const drawerApps = useWorkspaceAppsFromBackend([])

  const handleSignOut = (): void => {
    localStorage.clear()
    sessionStorage.clear()
    const returnUrl = encodeURIComponent(window.location.origin)
    window.location.href = `${AUTH_BASE_URL}/logout?return_url=${returnUrl}`
  }

  const handleSwitchWorkspace = (): void => {
    clearWorkspaceCache()
    const returnUrl = encodeURIComponent(window.location.origin)
    window.location.href = `${AUTH_BASE_URL}/select-workspace?return_url=${returnUrl}`
  }

  const workspaceMenuItems = buildWorkspaceMenuItems(workspaces, handleSwitchWorkspace)

  return (
    <>
      <div className="flex items-center el-gap-md flex-1 min-w-0">
        <BreadcrumbNav />
      </div>
      <div className="flex items-center el-gap-md flex-1 justify-end">
        <BookmarkButton />
        <NotificationBell />
        <AppDrawer align="end" apps={drawerApps} currentAppId="accounting" />
        <UserInfo
          compact
          authUrl={AUTH_BASE_URL}
          entityName={entityName ?? undefined}
          menuItems={workspaceMenuItems}
          user={{
            name: userProfile.displayName || 'User',
            email: userProfile.email || undefined,
            phone: userProfile.phone || undefined,
            avatar: userProfile.avatar || undefined,
          }}
          onSignOut={handleSignOut}
        />
      </div>
    </>
  )
}
